// src/features/Navigation/pages/Navigation/Navigation.tsx
import { useState } from "react";
import Page from "src/layouts/Page";
import useNavigationItems from "../../hooks/useNavigationItems";
import useCreateNavigationItem from "../../hooks/useCreateNavigationItem";
import useEditNavigationItem from "../../hooks/useEditNavigationItem";
import useDeleteNavigationItem from "../../hooks/useDeleteNavigationItem";
import useMoveNavigationItem from "../../hooks/useMoveNavigationItem";
import {
	adaptCreateNavigationItemPayload,
	adaptEditNavigationItemPayload
} from "../../adapters/adaptNavigationItemPayload";
import { buildNavigationTree } from "../../utils/buildNavigationTree";
import NavigationTree from "../../components/NavigationTree/NavigationTree";
import NavigationEditPanel from "../../components/NavigationEditPanel/NavigationEditPanel";
import NavigationEmptyState from "../../components/NavigationEmptyState/NavigationEmptyState";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";

const Navigation = () => {
	const { data: items = [], isLoading, isError } = useNavigationItems();
	const { mutate: createItem, isLoading: isCreating } =
		useCreateNavigationItem();
	const { mutate: editItem, isLoading: isEditing } = useEditNavigationItem();
	const { mutate: deleteItem, isLoading: isDeleting } =
		useDeleteNavigationItem();
	const { mutate: moveItem } = useMoveNavigationItem();

	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [isNewItem, setIsNewItem] = useState(false);
	const [newItemParent, setNewItemParent] = useState<string | null>(null);
	const [itemToDelete, setItemToDelete] = useState<string | null>(null);

	const tree = buildNavigationTree(items);
	const selectedItem = selectedId
		? (items.find((i) => i.id === selectedId) ?? null)
		: null;

	const handleSelect = (item: NavigationItem) => {
		setSelectedId(item.id);
		setIsNewItem(false);
	};

	const handleAddTopLevel = () => {
		if (isCreating) return;
		setSelectedId(null);
		setNewItemParent(null);
		setIsNewItem(true);
	};

	const handleAddChild = (parentId: string) => {
		if (isCreating) return;
		setSelectedId(null);
		setNewItemParent(parentId);
		setIsNewItem(true);
	};

	const handleClose = () => {
		setSelectedId(null);
		setIsNewItem(false);
	};

	const handleSave = (
		values: NavigationItemFormValues,
		imageReference: string | null,
		imageRemoved: boolean
	) => {
		if (isNewItem) {
			const payload = adaptCreateNavigationItemPayload(
				{
					...values,
					parent: newItemParent || values.parent
				},
				imageReference
			);
			createItem(payload, {
				onSuccess: () => {
					setIsNewItem(false);
				}
			});
		} else if (selectedItem) {
			const payload = adaptEditNavigationItemPayload(
				values,
				imageReference,
				imageRemoved
			);
			editItem({ id: selectedItem.id, payload });
		}
	};

	const handleDelete = (id: string) => {
		setItemToDelete(id);
	};

	const handleConfirmDelete = () => {
		if (!itemToDelete || isDeleting) return;
		deleteItem(itemToDelete, {
			onSuccess: () => {
				setItemToDelete(null);
				if (selectedId === itemToDelete) {
					setSelectedId(null);
				}
			}
		});
	};

	const handleMoveUp = (itemId: string) => {
		moveItem({ id: itemId, offset: -1 });
	};

	const handleMoveDown = (itemId: string) => {
		moveItem({ id: itemId, offset: 1 });
	};

	if (isLoading) {
		return (
			<Page>
				<div className="flex items-center justify-center py-20">
					<span className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
				</div>
			</Page>
		);
	}

	if (isError) {
		return (
			<Page>
				<div className="flex items-center justify-center py-20 text-muted-foreground">
					Failed to load navigation items.
				</div>
			</Page>
		);
	}

	if (items.length === 0 && !isNewItem) {
		return (
			<Page>
				<NavigationEmptyState onCreate={handleAddTopLevel} />
			</Page>
		);
	}

	const showPanel = isNewItem || selectedId !== null;
	const panelItem = isNewItem
		? newItemParent
			? ({ parent: newItemParent } as NavigationItem)
			: null
		: selectedItem;

	return (
		<Page>
			<div className="flex gap-4 h-[calc(100vh-180px)]">
				{/* Tree panel — hidden on mobile when edit panel is open */}
				<div className={showPanel ? "hidden xl:block xl:w-[60%]" : "w-full"}>
					<NavigationTree
						tree={tree}
						selectedId={selectedId}
						onSelect={handleSelect}
						onAddTopLevel={handleAddTopLevel}
						onAddChild={handleAddChild}
						onMoveUp={handleMoveUp}
						onMoveDown={handleMoveDown}
						onDelete={handleDelete}
					/>
				</div>

				{/* Edit panel — full width on mobile, 40% on desktop */}
				{showPanel && (
					<div className="w-full xl:w-[40%]">
						<NavigationEditPanel
							item={panelItem}
							allItems={items}
							onSave={handleSave}
							onDelete={handleDelete}
							onClose={handleClose}
							isSaving={isCreating || isEditing}
						/>
					</div>
				)}
			</div>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete navigation item?"
				description="This action cannot be undone. Deleting a parent will also delete all its children."
				onCancel={() => setItemToDelete(null)}
				onSubmit={handleConfirmDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default Navigation;
